import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  VendorDetailApproveReqDTO,
  VendorDetailRequestDTO,
  VendorDetailRespDTO,
  VendorDetailSearchReqDTO,
  VendorDetailSearchResDTO,
  VendorDocumentDetailRespDTO,
  VendorHistorySearchResDTO,
  VendorRejectDTO,
  VendorRequestDTO,
  addVendorSubscriptionReq
} from 'src/models/vendor';
import { userManagementAPIRoutes } from 'src/services/user-management';
import { vendorManagementAPIRoutes } from '..';

export const vendorApprovalServices = createApi({
  reducerPath: 'vendorApprovalService',
  tagTypes: ['VendorTag'],
  baseQuery: fetchBaseQuery({
    baseUrl: vendorManagementAPIRoutes.baseUrl,
    prepareHeaders: (headers) => {
      // If we have a token set in local storage, let's assume that we should be passing it.
      const token = localStorage.getItem('jwtToken');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  endpoints: (builder) => ({
    createVendor: builder.mutation<VendorDetailRespDTO, VendorRequestDTO>({
      query: (body) => ({
        url: `${userManagementAPIRoutes.baseUrl}/${userManagementAPIRoutes.users.endpoint}/${userManagementAPIRoutes.users.saveVendor}`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['VendorTag']
    }),
    findVendorDetailById: builder.query<VendorDetailRequestDTO, number>({
      query: (id) => ({
        url: `${vendorManagementAPIRoutes.vendor.endpoints}/${vendorManagementAPIRoutes.vendor.findById}/${id}`,
        method: 'GET'
      }),
      providesTags: ['VendorTag']
    }),
    getVendorDetailByUserId: builder.query<VendorDetailRequestDTO, number>({
      query: (userId) => ({
        url: `${vendorManagementAPIRoutes.vendor.endpoints}/${vendorManagementAPIRoutes.vendor.getVendorDetailByUserId}/${userId}`,
        method: 'GET'
      }),
      providesTags: ['VendorTag']
    }),
    updateVendorDetail: builder.mutation<
      VendorDetailRequestDTO,
      VendorDetailRequestDTO
    >({
      query: (body) => ({
        url: `${vendorManagementAPIRoutes.vendor.endpoints}/${vendorManagementAPIRoutes.vendor.updateVendorDetail}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: ['VendorTag']
    }),
    getVendorDetailBySearchCriteria: builder.query<
      VendorDetailSearchResDTO,
      VendorDetailSearchReqDTO
    >({
      query: (body) => ({
        url: `${vendorManagementAPIRoutes.vendor.endpoints}/${vendorManagementAPIRoutes.vendor.getAllVendorDetailBySearchCriteria}`,
        method: 'POST',
        body
      }),
      providesTags: ['VendorTag']
    }),
    getVendorDocumentDetail: builder.query<
      VendorDocumentDetailRespDTO[],
      { id: number }
    >({
      query: ({ id }) => ({
        url: `${vendorManagementAPIRoutes.vendor.endpoints}/${vendorManagementAPIRoutes.vendor.getVendorDocumentDetail}/${id}`,
        method: 'GET'
      }),
      providesTags: ['VendorTag']
    }),
    updateVendorApproval: builder.mutation<
      VendorDetailRequestDTO,
      VendorDetailApproveReqDTO
    >({
      query: (body) => ({
        url: `${vendorManagementAPIRoutes.vendor.endpoints}/${vendorManagementAPIRoutes.vendor.approveVendorDetail}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: ['VendorTag']
    }),
    updateVendorReject: builder.mutation<boolean, VendorRejectDTO>({
      query: (body) => ({
        url: `${vendorManagementAPIRoutes.vendor.endpoints}/${vendorManagementAPIRoutes.vendor.rejectVendorDetail}/`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['VendorTag']
    }),
    deleteVendorDetail: builder.mutation<boolean, number>({
      query: (id) => ({
        url: `${vendorManagementAPIRoutes.vendor.endpoints}/${vendorManagementAPIRoutes.vendor.deleteVendorDetal}?id=${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['VendorTag']
    }),
    findByDomainName: builder.mutation({
      query: ({ domainName }) => ({
        url: `${vendorManagementAPIRoutes.vendor.endpoints}/${vendorManagementAPIRoutes.vendor.findByDomainName}/${domainName}`,
        method: 'GET'
      }),
      invalidatesTags: ['VendorTag']
    }),
    getVendorHistory: builder.query<
      VendorHistorySearchResDTO,
      VendorDetailSearchReqDTO
    >({
      query: (body) => ({
        url: `${vendorManagementAPIRoutes.vendor.endpoints}/${vendorManagementAPIRoutes.vendor.vendorHistory}`,
        method: 'POST',
        body
      }),
      providesTags: ['VendorTag']
    }),
    addVendorSubscription: builder.mutation<
      addVendorSubscriptionReq,
      addVendorSubscriptionReq
    >({
      query: (body) => ({
        url: `${vendorManagementAPIRoutes.vendor.endpoints}/${vendorManagementAPIRoutes.vendor.addVendorSubscription}`,
        method: 'POST',
        body
      })
    }),
    getVendorSubscription: builder.query<any, number>({
      query: (id) => ({
        url: `${vendorManagementAPIRoutes.vendor.endpoints}/${vendorManagementAPIRoutes.vendor.checkVendorSubscription}/${id}`,
        method: 'GET'
      })
    }),
    extendSubscription: builder.mutation<any, { vendorId: number; body: { days: number; reason: string } }>({
      query: ({ vendorId, body }) => ({
        url: `subscription/extend/${vendorId}`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['VendorTag']
    }),
    recordOfflinePayment: builder.mutation<
      any,
      { vendorId: number; body: { amount: number; paymentMethod: string; referenceNumber: string; notes: string } }
    >({
      query: ({ vendorId, body }) => ({
        url: `subscription/payment/${vendorId}`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['VendorTag']
    }),
    getSubscriptionHistory: builder.query<any[], number>({
      query: (vendorId) => ({
        url: `subscription/history/${vendorId}`,
        method: 'GET'
      }),
      providesTags: ['VendorTag']
    }),
    getPaymentHistory: builder.query<any[], number>({
      query: (vendorId) => ({
        url: `subscription/payments/${vendorId}`,
        method: 'GET'
      }),
      providesTags: ['VendorTag']
    }),
    getLatestSubscription: builder.query<any, number>({
      query: (vendorId) => ({
        url: `subscription/latest/${vendorId}`,
        method: 'GET'
      }),
      providesTags: ['VendorTag']
    })
  })
});
export const {
  useCreateVendorMutation,
  useFindVendorDetailByIdQuery,
  useGetVendorDetailByUserIdQuery,
  useUpdateVendorDetailMutation,
  useGetVendorDetailBySearchCriteriaQuery,
  useGetVendorDocumentDetailQuery,
  useUpdateVendorApprovalMutation,
  useUpdateVendorRejectMutation,
  useDeleteVendorDetailMutation,
  useFindByDomainNameMutation,
  useGetVendorHistoryQuery,
  useAddVendorSubscriptionMutation,
  useGetVendorSubscriptionQuery,
  useExtendSubscriptionMutation,
  useRecordOfflinePaymentMutation,
  useGetSubscriptionHistoryQuery,
  useGetPaymentHistoryQuery,
  useGetLatestSubscriptionQuery
} = vendorApprovalServices;

